<template>
  <div class="app-container">
    <!-- Header Section -->
    <div class="header-section">
      <h2 class="page-title">Panel Administratora FTP</h2>
      <p class="page-subtitle">Zarządzaj żądaniami kont FTP i twórz nowe konta</p>
    </div>

    <!-- Tab Navigation -->
    <div class="tab-navigation">
      <el-button 
        :type="currentTab === 'requests' ? 'primary' : ''" 
        @click="currentTab = 'requests'"
      >
        Żądania FTP
      </el-button>
      <el-button 
        :type="currentTab === 'accounts' ? 'primary' : ''" 
        @click="currentTab = 'accounts'"
      >
        Konta FTP
      </el-button>
      <el-button 
        :type="currentTab === 'create' ? 'primary' : ''" 
        @click="currentTab = 'create'"
      >
        Dodaj nowe połączenie
      </el-button>
    </div>

    <!-- Statistics Cards -->
    <el-row :gutter="20" class="stats-cards">
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-number">{{ stats.pending }}</div>
            <div class="stat-label">Oczekujące żądania</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-number">{{ stats.approved }}</div>
            <div class="stat-label">Zakończone żądania</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-number">{{ stats.active }}</div>
            <div class="stat-label">Aktywne konta</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-number">{{ stats.disabled }}</div>
            <div class="stat-label">Wyłączone konta</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- FTP Requests Tab -->
    <div v-if="currentTab === 'requests'" class="tab-content">
      <el-card class="requests-card">
        <div slot="header" class="card-header">
          <h3>Żądania kont FTP</h3>
          <p>Przeglądaj i zarządzaj żądaniami kont FTP od użytkowników</p>
        </div>

        <el-table 
          v-loading="loading" 
          :data="ftpRequests" 
          border 
          style="width: 100%"
        >
          <el-table-column label="Użytkownik" min-width="200">
            <template slot-scope="scope">
              <div class="user-cell">
                <div class="user-name">{{ scope.row.user && scope.row.user.profile ? scope.row.user.profile.name : '-' }}</div>
                <div class="user-email">{{ scope.row.user ? scope.row.user.email : '-' }}</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="Data żądania" width="150" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.created_at) }}
            </template>
          </el-table-column>

          <el-table-column label="Status" width="150" align="center">
            <template slot-scope="scope">
              <el-tag 
                :type="getStatusType(scope.row.status)" 
                size="small"
              >
                <i :class="getStatusIcon(scope.row.status)"></i>
                {{ getStatusLabel(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="Akcje" width="250" align="center">
            <template slot-scope="scope">
              <div v-if="scope.row.status === 'pending'">
                <el-button 
                  type="success" 
                  size="small" 
                  icon="el-icon-check"
                  @click="handleApprove(scope.row)"
                >
                  Zaakceptuj
                </el-button>
                <el-button 
                  type="danger" 
                  size="small" 
                  icon="el-icon-close"
                  @click="handleReject(scope.row)"
                >
                  Odrzuć
                </el-button>
              </div>
              <div v-else>
                <el-button 
                  type="primary" 
                  size="small" 
                  icon="el-icon-view"
                  @click="handleViewDetails(scope.row)"
                >
                  Zobacz
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div v-if="ftpRequests.length === 0 && !loading" class="empty-state">
          <i class="el-icon-folder-opened"></i>
          <p>Brak żądań FTP</p>
        </div>
      </el-card>
    </div>

    <!-- FTP Accounts Tab -->
    <div v-if="currentTab === 'accounts'" class="tab-content">
      <el-card class="accounts-card">
        <div slot="header" class="card-header">
          <h3>Konta FTP</h3>
          <p>Zarządzaj utworzonymi kontami FTP użytkowników</p>
        </div>

        <el-table 
          v-loading="loading" 
          :data="activeIntegrations" 
          border 
          style="width: 100%"
        >
          <el-table-column label="Użytkownik" min-width="200">
            <template slot-scope="scope">
              <div class="user-cell">
                <div class="user-name">{{ scope.row.user && scope.row.user.profile ? scope.row.user.profile.name : '-' }}</div>
                <div class="user-email">{{ scope.row.user ? scope.row.user.email : '-' }}</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="Login FTP" width="200">
            <template slot-scope="scope">
              <code>{{ scope.row.ftp_login }}</code>
            </template>
          </el-table-column>

          <el-table-column label="Status" width="120" align="center">
            <template slot-scope="scope">
              <el-tag 
                :type="scope.row.is_active ? 'success' : 'info'" 
                size="small"
              >
                {{ scope.row.is_active ? 'Aktywne' : 'Wyłączone' }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="Statystyki" width="180" align="center">
            <template slot-scope="scope">
              <div class="stats-cell">
                <div>Plików: {{ scope.row.files_imported || 0 }}</div>
                <div v-if="scope.row.last_sync_at">
                  Ostatni sync: {{ formatDate(scope.row.last_sync_at) }}
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="Akcje" width="200" align="center">
            <template slot-scope="scope">
              <el-button 
                :type="scope.row.is_active ? 'warning' : 'success'" 
                size="small" 
                @click="handleToggleIntegration(scope.row)"
              >
                {{ scope.row.is_active ? 'Wyłącz' : 'Włącz' }}
              </el-button>
              <el-button 
                type="primary" 
                size="small" 
                icon="el-icon-view"
                @click="handleViewIntegration(scope.row)"
              >
                Szczegóły
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div v-if="activeIntegrations.length === 0 && !loading" class="empty-state">
          <i class="el-icon-folder-checked"></i>
          <p>Brak aktywnych kont FTP</p>
        </div>
      </el-card>
    </div>

    <!-- Create FTP Account Tab -->
    <div v-if="currentTab === 'create'" class="tab-content">
      <el-card class="create-card">
        <div slot="header" class="card-header">
          <h3>Dodaj nowe połączenie</h3>
          <p>Wprowadź dane aby ręcznie dodać połączenie API lub FTP dla użytkownika</p>
        </div>

        <el-form 
          ref="createForm" 
          :model="createForm" 
          :rules="createRules" 
          label-width="150px"
          label-position="top"
        >
          <el-form-item label="Nazwa użytkownika *" prop="user_id">
            <el-select 
              v-model="createForm.user_id" 
              filterable 
              remote
              reserve-keyword
              placeholder="Wprowadź nazwę użytkownika"
              :remote-method="handleUserSearch"
              :loading="userSearchLoading"
              style="width: 100%"
            >
              <el-option
                v-for="user in searchedUsers"
                :key="user.id"
                :label="`${user.name} (${user.email})`"
                :value="user.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="Typ integracji *" prop="integration_type">
            <el-select v-model="createForm.integration_type" style="width: 100%">
              <el-option label="IMO CRM" value="imo" />
              <el-option label="EstiCRM" value="esti" />
              <el-option label="Asari CRM" value="asari" />
              <el-option label="Inny FTP" value="custom" />
            </el-select>
            <div class="form-help-text">
              <i class="el-icon-info"></i>
              <span>IMO CRM - dla użytkowników systemu IMO, EstiCRM - dla użytkowników systemu EstiCRM, Asari CRM - dla użytkowników systemu Asari, Inny FTP - dla innych systemów CRM</span>
            </div>
          </el-form-item>

          <el-form-item label="Typ połączenia *" prop="connection_type">
            <el-select v-model="createForm.connection_type" style="width: 100%">
              <el-option label="FTP" value="FTP" />
            </el-select>
          </el-form-item>

          <el-row :gutter="20">
            <el-col :span="16">
              <el-form-item label="Host *" prop="ftp_server">
                <el-input 
                  v-model="createForm.ftp_server" 
                  placeholder="ftp.example.com"
                  disabled
                  class="disabled-input"
                />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="Port *" prop="ftp_port">
                <el-input 
                  v-model="createForm.ftp_port" 
                  disabled
                  class="disabled-input"
                />
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item label="Użytkownik FTP *" prop="ftp_login">
            <el-input 
              v-model="createForm.ftp_login" 
              placeholder="Nazwa użytkownika FTP"
              disabled
            >
            </el-input>
            <div class="form-help-text">
              <i class="el-icon-info"></i>
              <span>Login zostanie wygenerowany automatycznie po utworzeniu konta</span>
            </div>
          </el-form-item>

          <el-form-item label="Hasło FTP *" prop="ftp_password">
            <el-input 
              v-model="createForm.ftp_password" 
              placeholder="Hasło"
              show-password
            >
              <el-button slot="append" icon="el-icon-refresh" @click="generatePassword">
                Generuj
              </el-button>
            </el-input>
          </el-form-item>

          <el-form-item label="Opis" prop="description">
            <el-input 
              v-model="createForm.description" 
              type="textarea"
              :rows="3"
              placeholder="Dodatkowy opis połączenia"
            />
          </el-form-item>

          <el-form-item>
            <el-button 
              type="primary" 
              :loading="creating"
              @click="handleCreateFtpAccount"
            >
              Dodaj połączenie
            </el-button>
            <el-button @click="resetCreateForm">Wyczyść</el-button>
          </el-form-item>
        </el-form>

        <!-- Recently Added Connections -->
        <div class="recent-connections">
          <h4>Ostatnio dodane połączenia</h4>
          <p class="subtitle">Lista połączeń dodanych w ciągu ostatnich 30 dni</p>
          
          <el-table 
            :data="recentConnections" 
            border 
            style="width: 100%"
            empty-text="Brak połączeń"
          >
            <el-table-column label="ID" prop="id" width="80" />
            <el-table-column label="Użytkownik" min-width="150">
              <template slot-scope="scope">
                {{ scope.row.user && scope.row.user.profile ? scope.row.user.profile.name : (scope.row.user ? scope.row.user.email : '-') }}
              </template>
            </el-table-column>
            <el-table-column label="Typ" prop="ftp_protocol" width="100" />
            <el-table-column label="Host/URL" min-width="150">
              <template slot-scope="scope">
                {{ scope.row.ftp_server }}:{{ scope.row.ftp_port }}
              </template>
            </el-table-column>
            <el-table-column label="Status" width="120" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.status === 'active' ? 'success' : 'info'" size="small">
                  {{ scope.row.status === 'active' ? 'Aktywne' : 'Nieaktywne' }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-card>
    </div>

    <!-- View Details Dialog -->
    <el-dialog 
      :visible.sync="detailsVisible" 
      title="Szczegóły żądania FTP"
      width="600px"
    >
      <div v-if="selectedRequest" class="details-content">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="Użytkownik">
            {{ selectedRequest.user && selectedRequest.user.profile ? selectedRequest.user.profile.name : '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="Email">
            {{ selectedRequest.user ? selectedRequest.user.email : '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="Data żądania">
            {{ formatDate(selectedRequest.created_at) }}
          </el-descriptions-item>
          <el-descriptions-item label="Status">
            <el-tag :type="getStatusType(selectedRequest.status)">
              {{ getStatusLabel(selectedRequest.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item v-if="selectedRequest.approved_at" label="Data zatwierdzenia">
            {{ formatDate(selectedRequest.approved_at) }}
          </el-descriptions-item>
          <el-descriptions-item v-if="selectedRequest.notes" label="Notatki">
            {{ selectedRequest.notes }}
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>

    <!-- View Integration Dialog -->
    <el-dialog 
      :visible.sync="integrationVisible" 
      title="Szczegóły integracji FTP"
      width="700px"
    >
      <div v-if="selectedIntegration" class="details-content">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="Użytkownik" :span="2">
            {{ selectedIntegration.user && selectedIntegration.user.profile ? selectedIntegration.user.profile.name : '' }} ({{ selectedIntegration.user ? selectedIntegration.user.email : '' }})
          </el-descriptions-item>
          <el-descriptions-item label="Login FTP">
            <code>{{ selectedIntegration.ftp_login }}</code>
          </el-descriptions-item>
          <el-descriptions-item label="Serwer">
            {{ selectedIntegration.ftp_server }}:{{ selectedIntegration.ftp_port }}
          </el-descriptions-item>
          <el-descriptions-item label="Protokół">
            {{ selectedIntegration.ftp_protocol.toUpperCase() }}
          </el-descriptions-item>
          <el-descriptions-item label="Status">
            <el-tag :type="selectedIntegration.is_active ? 'success' : 'info'">
              {{ selectedIntegration.is_active ? 'Aktywne' : 'Wyłączone' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="Data utworzenia">
            {{ formatDate(selectedIntegration.created_at) }}
          </el-descriptions-item>
          <el-descriptions-item label="Ostatnia synchronizacja">
            {{ selectedIntegration.last_sync_at ? formatDate(selectedIntegration.last_sync_at) : 'Brak' }}
          </el-descriptions-item>
          <el-descriptions-item label="Zaimportowanych plików">
            {{ selectedIntegration.files_imported || 0 }}
          </el-descriptions-item>
          <el-descriptions-item label="Współczynnik sukcesu">
            {{ selectedIntegration.success_rate || 0 }}%
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import IntegrationResource from '@/api/integration';

const integrationResource = new IntegrationResource();

export default {
  name: 'FtpRequests',
  data() {
    return {
      currentTab: 'requests',
      loading: false,
      creating: false,
      userSearchLoading: false,
      ftpRequests: [],
      activeIntegrations: [],
      searchedUsers: [],
      recentConnections: [],
      stats: {
        pending: 0,
        approved: 0,
        active: 0,
        disabled: 0,
      },
      createForm: {
        user_id: '',
        integration_type: 'imo',
        connection_type: 'FTP',
        ftp_login: '',
        ftp_password: '',
        ftp_server: 'ftp.dazu.pl',
        ftp_port: '21',
        ftp_protocol: 'FTP',
        description: '',
      },
      createRules: {
        user_id: [{ required: true, message: 'Wybierz użytkownika', trigger: 'change' }],
        ftp_password: [
          { required: true, message: 'Wpisz hasło FTP', trigger: 'blur' },
          { min: 8, message: 'Hasło musi mieć minimum 8 znaków', trigger: 'blur' },
        ],
      },
      detailsVisible: false,
      integrationVisible: false,
      selectedRequest: null,
      selectedIntegration: null,
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadFtpRequests(),
          this.loadIntegrations(),
          this.loadRecentConnections(),
        ]);
        this.calculateStats();
      } catch (error) {
        console.error('Error loading data:', error);
        this.$message.error('Błąd podczas ładowania danych');
      } finally {
        this.loading = false;
      }
    },

    async loadFtpRequests() {
      try {
        const response = await integrationResource.getFtpRequests();
        this.ftpRequests = response.data || [];
      } catch (error) {
        console.error('Error loading FTP requests:', error);
      }
    },

    async loadIntegrations() {
      try {
        const response = await integrationResource.getIntegrations();
        this.activeIntegrations = response.data || [];
      } catch (error) {
        console.error('Error loading integrations:', error);
      }
    },

    async loadRecentConnections() {
      try {
        const response = await integrationResource.getIntegrations();
        // Filter connections created in last 30 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        this.recentConnections = (response.data || []).filter(conn => {
          const createdAt = new Date(conn.created_at);
          return createdAt >= thirtyDaysAgo;
        }).slice(0, 10);
      } catch (error) {
        console.error('Error loading recent connections:', error);
      }
    },

    async handleUserSearch(query) {
      if (query.length < 2) {
        this.searchedUsers = [];
        return;
      }
      
      this.userSearchLoading = true;
      try {
        const response = await integrationResource.searchUsers(query);
        this.searchedUsers = response || [];
      } catch (error) {
        console.error('Error searching users:', error);
        this.searchedUsers = [];
      } finally {
        this.userSearchLoading = false;
      }
    },

    calculateStats() {
      this.stats.pending = this.ftpRequests.filter(r => r.status === 'pending').length;
      this.stats.approved = this.ftpRequests.filter(r => r.status === 'approved').length;
      this.stats.active = this.activeIntegrations.filter(i => i.is_active).length;
      this.stats.disabled = this.activeIntegrations.filter(i => !i.is_active).length;
    },

    async handleApprove(request) {
      try {
        await this.$confirm('Czy na pewno chcesz zaakceptować to żądanie FTP?', 'Potwierdzenie', {
          confirmButtonText: 'Tak, zaakceptuj',
          cancelButtonText: 'Anuluj',
          type: 'success',
        });

        this.loading = true;
        await integrationResource.approveFtpRequest(request.id);
        this.$message.success('Żądanie FTP zostało zaakceptowane');
        await this.loadData();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error approving request:', error);
          this.$message.error('Błąd podczas akceptacji żądania');
        }
      } finally {
        this.loading = false;
      }
    },

    async handleReject(request) {
      try {
        const { value: reason } = await this.$prompt('Podaj powód odrzucenia', 'Odrzuć żądanie', {
          confirmButtonText: 'Odrzuć',
          cancelButtonText: 'Anuluj',
          inputPlaceholder: 'Wpisz powód odrzucenia...',
          inputValidator: (value) => {
            if (!value || value.trim() === '') {
              return 'Musisz podać powód odrzucenia';
            }
            return true;
          },
        });

        this.loading = true;
        await integrationResource.rejectFtpRequest(request.id, reason);
        this.$message.success('Żądanie FTP zostało odrzucone');
        await this.loadData();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error rejecting request:', error);
          this.$message.error('Błąd podczas odrzucania żądania');
        }
      } finally {
        this.loading = false;
      }
    },

    async handleToggleIntegration(integration) {
      const action = integration.is_active ? 'wyłączyć' : 'włączyć';
      try {
        await this.$confirm(`Czy na pewno chcesz ${action} tę integrację?`, 'Potwierdzenie', {
          confirmButtonText: 'Tak',
          cancelButtonText: 'Anuluj',
          type: 'warning',
        });

        this.loading = true;
        await integrationResource.toggleIntegration(integration.id);
        this.$message.success(`Integracja została ${integration.is_active ? 'wyłączona' : 'włączona'}`);
        await this.loadData();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error toggling integration:', error);
          this.$message.error('Błąd podczas zmiany statusu integracji');
        }
      } finally {
        this.loading = false;
      }
    },

    handleViewDetails(request) {
      this.selectedRequest = request;
      this.detailsVisible = true;
    },

    handleViewIntegration(integration) {
      this.selectedIntegration = integration;
      this.integrationVisible = true;
    },

    async handleCreateFtpAccount() {
      this.$refs.createForm.validate(async (valid) => {
        if (!valid) {
          return false;
        }

        try {
          this.creating = true;
          const data = {
            user_id: this.createForm.user_id,
            integration_type: this.createForm.integration_type,
            password: this.createForm.ftp_password,
            description: this.createForm.description,
          };
          
          await integrationResource.createManualFtpAccount(data);
          this.$message.success('Konto FTP zostało utworzone pomyślnie. Dane dostępowe zostały wysłane na email użytkownika.');
          this.resetCreateForm();
          await this.loadData();
        } catch (error) {
          console.error('Error creating FTP account:', error);
          const errorMsg = (error.response && error.response.data && error.response.data.message) || 'Błąd podczas tworzenia konta FTP';
          this.$message.error(errorMsg);
        } finally {
          this.creating = false;
        }
      });
    },

    resetCreateForm() {
      if (this.$refs.createForm) {
        this.$refs.createForm.resetFields();
      }
      this.createForm = {
        user_id: '',
        integration_type: 'imo',
        connection_type: 'FTP',
        ftp_login: '',
        ftp_password: '',
        ftp_server: 'ftp.dazu.pl',
        ftp_port: '21',
        ftp_protocol: 'FTP',
        description: '',
      };
      this.searchedUsers = [];
    },

    async generatePassword() {
      try {
        const response = await integrationResource.generatePassword();
        this.createForm.ftp_password = response.password;
        this.$message.success('Hasło zostało wygenerowane');
      } catch (error) {
        // Fallback to local generation
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
        let password = '';
        for (let i = 0; i < 16; i++) {
          password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        this.createForm.ftp_password = password;
        this.$message.success('Hasło zostało wygenerowane');
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('pl-PL');
    },

    getStatusType(status) {
      const types = {
        pending: 'warning',
        approved: 'success',
        rejected: 'danger',
      };
      return types[status] || 'info';
    },

    getStatusIcon(status) {
      const icons = {
        pending: 'el-icon-time',
        approved: 'el-icon-check',
        rejected: 'el-icon-close',
      };
      return icons[status] || '';
    },

    getStatusLabel(status) {
      const labels = {
        pending: 'Oczekujące',
        approved: 'Zatwierdzone',
        rejected: 'Odrzucone',
      };
      return labels[status] || status;
    },
  },
};
</script>

<style scoped lang="scss">
.app-container {
  padding: 20px;
}

.header-section {
  margin-bottom: 30px;
  text-align: center;

  .page-title {
    font-size: 28px;
    font-weight: 600;
    color: #303133;
    margin: 0 0 10px 0;
  }

  .page-subtitle {
    font-size: 14px;
    color: #909399;
    margin: 0;
  }
}

.tab-navigation {
  margin-bottom: 20px;
  text-align: center;

  .el-button {
    margin: 0 5px;
  }
}

.stats-cards {
  margin-bottom: 30px;

  .stat-card {
    text-align: center;
    margin-bottom: 15px;

    .stat-content {
      padding: 10px 0;

      .stat-number {
        font-size: 36px;
        font-weight: 700;
        color: #409EFF;
        margin-bottom: 8px;
      }

      .stat-label {
        font-size: 14px;
        color: #909399;
      }
    }
  }
}

.tab-content {
  .card-header {
    h3 {
      margin: 0 0 5px 0;
      font-size: 18px;
      font-weight: 600;
    }

    p {
      margin: 0;
      font-size: 13px;
      color: #909399;
    }
  }
}

.user-cell {
  .user-name {
    font-weight: 600;
    color: #303133;
    margin-bottom: 3px;
  }

  .user-email {
    font-size: 12px;
    color: #606266;
    margin-bottom: 2px;
  }

  .user-company {
    font-size: 12px;
    color: #909399;
    font-style: italic;
  }
}

.stats-cell {
  font-size: 12px;
  color: #606266;

  div {
    margin-bottom: 2px;
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;

  i {
    font-size: 64px;
    margin-bottom: 15px;
    display: block;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

.details-content {
  padding: 10px 0;

  code {
    background: #f5f7fa;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
  }
}

.form-help-text {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f0f9ff;
  border-left: 3px solid #409EFF;
  border-radius: 4px;
  font-size: 13px;
  color: #606266;

  i {
    color: #409EFF;
    margin-right: 5px;
  }

  strong {
    color: #303133;
    font-family: 'Courier New', monospace;
    background: #e6f7ff;
    padding: 2px 4px;
    border-radius: 2px;
  }
}

.disabled-input {
  ::v-deep .el-input__inner {
    background-color: #f5f7fa;
    color: #909399;
    cursor: not-allowed;
  }
}

.recent-connections {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;

  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    margin: 0 0 5px 0;
  }

  .subtitle {
    font-size: 13px;
    color: #909399;
    margin: 0 0 15px 0;
  }
}
</style>
